import { SYSTEM_LISTS_DEFAULTS } from "./globals";
import { buildNextRecordNumber, mapCostCenterCodes } from "./recordNumberUtils";

const DEFAULT_COST_CENTER_CODE_MAP = mapCostCenterCodes(SYSTEM_LISTS_DEFAULTS?.COST_CENTER_CODES || []);

function normalizeText(value) {
  return String(value || "").trim();
}

function normalizeSpeciesValue(value) {
  const raw = normalizeText(value).toLowerCase();
  if (!raw) return "";
  if (raw.startsWith("bov")) return "Bovino";
  if (raw.startsWith("equ")) return "Equino";
  if (raw.startsWith("cap")) return "Caprino";
  if (raw.startsWith("ov")) return "Ovino";
  if (raw.startsWith("su")) return "Suíno";
  if (raw.startsWith("av") || raw.includes("galin")) return "Ave";
  if (raw.startsWith("out")) return "Outro";
  return "";
}

function resolveLivestockSuffixKey(record) {
  const normalized = normalizeSpeciesValue(record?.species || record?.animalSpecies || record?.speciesName);
  if (normalized === "Bovino") return "Bovinos";
  if (normalized === "Equino") return "Equinos";
  if (normalized === "Caprino") return "Caprinos";
  return "Outros";
}

function resolveModuleOptions(collectionName, record, options = {}) {
  const cleanCollection = normalizeText(collectionName);
  const costCenterCodeMap = options?.costCenterCodeMap || DEFAULT_COST_CENTER_CODE_MAP;

  switch (cleanCollection) {
    case "orders":
      return { moduleKey: "purchases", costCenter: record?.costCenter, costCenterCodeMap };
    case "services":
      return { moduleKey: "services", costCenter: record?.costCenter, costCenterCodeMap };
    case "occurrences":
      return { moduleKey: "occurrences", costCenter: record?.costCenter, costCenterCodeMap };
    case "inspections":
      return { moduleKey: "inspections", costCenter: record?.costCenter, costCenterCodeMap };
    case "machinery":
      return { moduleKey: "machinery" };
    case "stock":
      return { moduleKey: "stock", costCenter: record?.costCenter, costCenterCodeMap };
    case "financial":
      return { moduleKey: "financial", costCenter: record?.costCenter, costCenterCodeMap };
    case "employees":
      return { moduleKey: "employees" };
    case "vet":
      return { moduleKey: "veterinary" };
    case "harasEquines":
    case "harasStalls":
      return { moduleKey: "haras" };
    case "pastures":
    case "poi":
      return { moduleKey: "locations" };
    case "animals":
    case "animalLots": {
      const species = normalizeSpeciesValue(record?.species || record?.animalSpecies || record?.speciesName);
      if (species === "Ave") return { moduleKey: "birds" };
      return { moduleKey: "animals", livestockType: resolveLivestockSuffixKey(record) };
    }
    default:
      return null;
  }
}

function supportsRecordNumberMigration(collectionName) {
  return !!resolveModuleOptions(collectionName, {});
}

function migrateCollectionRecordNumbers(collectionName, records = [], options = {}) {
  if (!supportsRecordNumberMigration(collectionName)) {
    return { records: Array.isArray(records) ? records : [], changed: false };
  }

  const input = Array.isArray(records) ? records : [];
  const working = input.map((item) => (item && typeof item === "object" ? { ...item } : item));
  let changed = false;

  working.forEach((record, index) => {
    if (!record || typeof record !== "object" || normalizeText(record.recordNumber)) return;
    const moduleOptions = resolveModuleOptions(collectionName, record, options);
    if (!moduleOptions?.moduleKey) return;
    const nextRecordNumber = buildNextRecordNumber(working.filter(Boolean), moduleOptions);
    working[index] = { ...record, ...nextRecordNumber };
    changed = true;
  });

  return {
    records: changed ? working : input,
    changed,
  };
}

export {
  supportsRecordNumberMigration,
  migrateCollectionRecordNumbers,
  resolveModuleOptions,
};
